<script setup lang="ts">
defineProps<{
  user?: any
}>();

defineOptions({ name: 'Navbar' });
import { RouterLink, useRoute } from 'vue-router';
import { inject, computed, ref, type Ref, onMounted, onUnmounted } from 'vue';
import IconConfig from './icons/IconConfig.vue';

const user = inject<any>('user', null);
// Reemplaza la lógica de isAdmin con una basada en roles
const userRoles = inject<Ref<string[]>>('userRoles', ref([]));

// Función para verificar si el usuario tiene un rol específico
const hasRole = (roleName: string) => {
  return userRoles.value.includes(roleName);
};

// Función para verificar si el usuario tiene al menos uno de los roles requeridos
const hasAnyRole = (requiredRoles: string[]) => {
  return requiredRoles.some(role => userRoles.value.includes(role));
};

// Definición de menús principales con control de permisos basado en roles
const menuItems = computed(() => ({
  grupos: {
    show: hasAnyRole(['admin', 'group_leader']), // Mostrar si es admin o group_leader
    submenu: [
      { to: '/grupos/', text: 'Todos los grupos', requiredRoles: ['admin'] },
      { to: '/mis-grupos/', text: 'Mis grupos', requiredRoles: ['group_leader'] },
    ]
  },
  eventos: {
    show: true,
    submenu: [
      { to: '/events/', text: 'Eventos de hoy' },
      { to: '/events/proximos', text: 'Próximos eventos' },
      { to: '/events/pasados', text: 'Eventos pasados' },
      { to: '/asistencias/', text: 'Asistencias', requiredRoles: ['admin', 'assistance_manager'] }
    ]
  },
  reportes: {
    show: hasAnyRole(['admin', 'reports_viewer']), // Nuevo menú de reportes
    submenu: [
      { to: '/reportes/eventos', text: 'Reportes de Eventos', requiredRoles: ['admin', 'reports_viewer'] },
      { to: '/reportes/usuarios', text: 'Reportes de Usuarios', requiredRoles: ['admin'] }
    ]
  },
  config: {
    show: hasRole('admin'), // Solo mostrar si es admin
    submenu: [
      { to: '/usuarios/', text: 'Usuarios', requiredRoles: ['admin'] },
      { to: '/ubicaciones/', text: 'Ubicaciones', requiredRoles: ['admin'] },
    ]
  }
}));

// Computed para obtener submenús filtrados por roles
const filteredSubmenus = computed(() => {
  const result: Record<string, any[]> = {};

  Object.entries(menuItems.value).forEach(([key, menu]) => {
    if (menu.show) {
      result[key] = menu.submenu.filter(item => {
        // Si el item requiere roles específicos, verificar si el usuario los tiene
        if (item.requiredRoles && !hasAnyRole(item.requiredRoles)) {
          return false;
        }
        return true;
      });
    }
  });

  return result;
});

// Obtener la ruta actual
const route = useRoute();

// Estado para controlar qué submenú está activo
const activeSubmenu = ref<string | null>(null);



// Función para verificar si una ruta padre tiene rutas hijas activas
const isParentRouteActive = (menuName: string) => {
  const menuSubmenu = filteredSubmenus.value[menuName];
  if (!menuSubmenu) return false;

  return menuSubmenu.some(item => {
    // Verificar coincidencia exacta
    if (route.path === item.to) return true;

    // Verificar si la ruta actual comienza con la ruta del item (para rutas anidadas)
    if (item.to !== '/' && route.path.startsWith(item.to.replace(/\/$/, ''))) {
      return true;
    }

    return false;
  });
};

// Computed para verificar si el menú tiene rutas activas
const isGruposActive = computed(() => isParentRouteActive('grupos'));
const isEventosActive = computed(() => isParentRouteActive('eventos'));
const isConfigActive = computed(() => isParentRouteActive('config'));

// Computed para verificar si un menú debe mostrarse (tiene al menos un elemento visible)
const shouldShowMenu = (menuName: string) => {
  const menu = menuItems.value[menuName as keyof typeof menuItems.value];
  if (!menu?.show) return false;

  const filteredItems = filteredSubmenus.value[menuName];
  return filteredItems && filteredItems.length > 0;
};

// Función para alternar la visibilidad del submenú
const toggleSubmenu = (menuName: string) => {
  // Solo abrir si el menú debe mostrarse
  if (!shouldShowMenu(menuName)) return;

  // Cierra el submenú actual si ya está abierto, o abre el nuevo
  activeSubmenu.value = activeSubmenu.value === menuName ? null : menuName;
};

// Cerrar todos los submenús
const closeAllSubmenus = () => {
  activeSubmenu.value = null;
};

// Manejar clics en elementos de navegación
const handleNavItemClick = (itemType: 'link' | 'submenu', menuName?: string) => {
  if (itemType === 'link') {
    // Si es un enlace normal, cerrar todos los submenús
    closeAllSubmenus();
  } else if (itemType === 'submenu' && menuName) {
    // Si es un elemento con submenú, alternar su estado
    toggleSubmenu(menuName);
  }
};

// Manejar clics fuera del submenú
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement;
  if (!target.closest('.has-submenu') && !target.closest('.submenu-container')) {
    closeAllSubmenus();
  }
};

// Agregar event listener cuando el componente se monta
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

// Remover event listener cuando el componente se desmonta
onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
  <!-- Overlay detrás de todo -->
  <div class="submenu-overlay" :class="{ 'overlay-open': activeSubmenu !== null }" @click="closeAllSubmenus"></div>

  <!-- Submenú desplegable (único contenedor para todos los submenús) -->
  <div class="submenu-container" :class="{ 'submenu-open': activeSubmenu !== null }">
    <!-- Submenú de Grupos -->
    <div v-if="shouldShowMenu('grupos')" class="submenu-content"
      :class="{ 'submenu-active': activeSubmenu === 'grupos' }">
      <RouterLink v-for="(item, index) in filteredSubmenus.grupos" :key="index" :to="item.to" class="submenu-item"
        @click="closeAllSubmenus">
        <span>{{ item.text }}</span>
      </RouterLink>
    </div>

    <!-- Submenú de Eventos -->
    <div v-if="shouldShowMenu('eventos')" class="submenu-content"
      :class="{ 'submenu-active': activeSubmenu === 'eventos' }">
      <RouterLink v-for="(item, index) in filteredSubmenus.eventos" :key="index" :to="item.to" class="submenu-item"
        @click="closeAllSubmenus">
        <span>{{ item.text }}</span>
      </RouterLink>
    </div>

    <!-- Submenú de Configuración -->
    <div v-if="shouldShowMenu('config')" class="submenu-content"
      :class="{ 'submenu-active': activeSubmenu === 'config' }">
      <RouterLink v-for="(item, index) in filteredSubmenus.config" :key="index" :to="item.to" class="submenu-item"
        @click="closeAllSubmenus">
        <span>{{ item.text }}</span>
      </RouterLink>
    </div>
  </div>

  <!-- Navbar principal -->
  <nav class="mobile-nav">
    <RouterLink to="/" class="nav-item" @click="handleNavItemClick('link')">
      <svg class="nav-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        stroke-width="2">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
        <polyline points="9 22 9 12 15 12 15 22"></polyline>
      </svg>
      <span class="nav-text">Inicio</span>
    </RouterLink>

    <RouterLink to="/chat" v-if="user && hasRole('can_chat')" class="nav-item" @click="handleNavItemClick('link')">
      <svg class="nav-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        stroke-width="2">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
      </svg>
      <span class="nav-text">Chat</span>
    </RouterLink>

    <!-- Elemento con submenú de Grupos (solo si debe mostrarse) -->
    <div v-if="shouldShowMenu('grupos')" class="nav-item has-submenu" :class="{
      active: activeSubmenu === 'grupos',
      'parent-route-active': isGruposActive
    }" @click="handleNavItemClick('submenu', 'grupos')">
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="nav-icon">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
        <circle cx="9" cy="7" r="4"></circle>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
      </svg>
      <span class="nav-text">Grupos</span>
    </div>

    <!-- Elemento con submenú de Eventos (solo si debe mostrarse) -->
    <div v-if="shouldShowMenu('eventos')" class="nav-item has-submenu" :class="{
      active: activeSubmenu === 'eventos',
      'parent-route-active': isEventosActive
    }" @click="handleNavItemClick('submenu', 'eventos')">
      <svg class="nav-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        stroke-width="2">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
        <line x1="16" y1="2" x2="16" y2="6"></line>
        <line x1="8" y1="2" x2="8" y2="6"></line>
        <line x1="3" y1="10" x2="21" y2="10"></line>
      </svg>
      <span class="nav-text">Eventos</span>
    </div>

    <!-- Elemento con submenú de Configuración (solo si debe mostrarse) -->
    <div v-if="shouldShowMenu('config')" class="nav-item has-submenu" :class="{
      active: activeSubmenu === 'config',
      'parent-route-active': isConfigActive
    }" @click="handleNavItemClick('submenu', 'config')">
      <IconConfig :size="24" class="nav-icon" />
      <span class="nav-text">Ajustes</span>
    </div>

    <RouterLink to="/profile" class="nav-item" v-if="user" @click="handleNavItemClick('link')">
      <svg class="nav-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        stroke-width="2">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
        <circle cx="12" cy="7" r="4"></circle>
      </svg>
      <span class="nav-text">Perfíl</span>
    </RouterLink>
  </nav>
</template>

<style scoped>
/* Navbar móvil en la parte inferior */
.mobile-nav {
  display: flex;
  /* position: fixed; */
  bottom: 0;
  left: 0;
  right: 0;
  background-color: #fff;
  box-shadow: 0 -2px 15px rgba(0, 0, 0, 0.1);
  z-index: 1002;
  /* Mayor z-index para que esté encima del overlay y submenú */
  flex: 0 0 auto;
}

.nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  text-decoration: none;
  color: #777;
  padding: 0.5rem 0;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
}

/* Solo aplicar color activo a rutas activas, no a submenús abiertos */
.nav-item.router-link-exact-active {
  color: #42b883;
  /* Color de Vue.js */
}

.nav-item.router-link-exact-active .nav-icon {
  transform: translateY(-3px);
}

/* Estilo para elementos con submenú cuando están activos (abiertos) */
.has-submenu.active {
  color: #42b883;
  /* Mismo color para indicar que el submenú está abierto */
}

.has-submenu.active .nav-icon {
  transform: translateY(-3px);
}

/* Estilo para elementos padre cuando tienen rutas hijas activas */
.has-submenu.parent-route-active {
  color: #42b883;
}

.has-submenu.parent-route-active .nav-icon {
  transform: translateY(-3px);
}

.nav-icon {
  width: 24px;
  height: 24px;
  margin-bottom: 0.25rem;
  transition: transform 0.3s ease;
}

.nav-text {
  font-size: 0.75rem;
  font-weight: 500;
  position: relative;
}

/* Indicador activo - para rutas activas */
.nav-item.router-link-exact-active::after {
  content: '';
  position: absolute;
  top: 0;
  width: 5px;
  height: 3px;
  background-color: #42b883;
  border-radius: 3px;
}

/* NUEVO: Indicador activo - para elementos padre con rutas hijas activas */
.has-submenu.parent-route-active::after {
  content: '';
  position: absolute;
  top: 0;
  width: 5px;
  height: 3px;
  background-color: #42b883;
  border-radius: 3px;
}

/* Contenedor del submenú (único para todos los submenús) */
.submenu-container {
  position: fixed;
  bottom: 63px;
  /* Altura del navbar */
  left: 0;
  right: 0;
  background-color: white;
  box-shadow: 0 -4px 10px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  z-index: 1001;
  /* Encima del overlay pero debajo del navbar */
  transform: translateY(100%);
  transition: transform 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.submenu-open {
  transform: translateY(0);
}

/* Contenido de cada submenú (todos ocultos por defecto) */
.submenu-content {
  display: none;
  flex-direction: column;
}

.submenu-active {
  display: flex;
}

.submenu-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  text-decoration: none;
  color: #333;
  font-size: 0.9rem;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s ease;
}

.submenu-item:last-child {
  border-bottom: none;
}

.submenu-item:hover {
  background-color: #f8f8f8;
}

.submenu-item.router-link-active {
  color: #42b883;
  font-weight: 500;
  background-color: #f0f9f5;
}

/* Overlay detrás de todo */
.submenu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.3);
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease, visibility 0.3s ease;
  z-index: 999;
  /* Debajo del submenú y navbar pero encima del contenido principal */
}

.overlay-open {
  opacity: 1;
  visibility: visible;
}

/* Pequeña flecha indicadora para elementos con submenú */
.has-submenu .nav-text::after {
  content: '';
  display: inline-block;
  margin-left: auto;
  margin-right: auto;
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 4px solid currentColor;
  transition: transform 0.3s ease;
  opacity: 0.7;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
}

.has-submenu.active .nav-text::after {
  transform: rotate(180deg);
}
</style>
